import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Combobox,
  ComboboxDropdown,
  ComboboxNewOption,
  ComboboxOption,
  ComboboxSelectButton,
  ComboboxSelectCheck,
  Content,
  Header,
  Heading,
  Icon,
  LoadingIcon,
  Text,
  Textarea,
  cn,
  useComboboxInputValue,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useCaptureEventOnMount } from '@eluve/posthog-react';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { useUserSpecialty } from '@eluve/smart-blocks';
import { MEDICAL_SPECIALTIES } from '@eluve/utils';

type NoteFormat = 'SOAP' | 'BIRP' | 'DAP' | 'GIRP';
type NoteLength = 'concise' | 'balanced' | 'detailed';

const VISIT_TYPES = [
  'New patient onboarding',
  'Initial consultation',
  'Follow-up visit',
  'Annual physical / wellness exam',
  'Urgent / same-day visit',
  'Telehealth check-in',
  'Therapy session',
  'Procedure visit',
  'Pre-operative consultation',
  'Post-operative follow-up',
  'Medication review',
  'Discharge / final visit',
];

/**
 * One-click instruction lines, inserted into (or removed from) the
 * instructions textarea. Length and visit type have their own controls,
 * so these stick to style and content directives.
 */
const POPULAR_KEYWORDS: { label: string; line: string }[] = [
  { label: 'Bullet points', line: 'Use bullet points where possible' },
  { label: 'ICD-10 codes', line: 'Include relevant ICD-10 codes' },
  { label: 'Patient-friendly', line: 'Use patient-friendly language' },
  { label: 'Omit normal findings', line: 'Omit normal findings' },
  { label: 'Telehealth visit', line: 'Optimize for a telehealth visit' },
  {
    label: 'Quote the patient',
    line: 'Quote the patient directly where it adds clarity',
  },
  {
    label: 'Medical necessity',
    line: 'Capture what is needed to support medical necessity for billing',
  },
  {
    label: 'Patient recap',
    line: 'End with a plain-language recap for the patient',
  },
  {
    label: 'Follow-up tasks',
    line: 'End with a clear list of follow-up tasks and timeframes',
  },
  {
    label: 'Medication list',
    line: 'Include a current medication list with doses',
  },
  {
    label: 'Differential diagnosis',
    line: 'Include a brief differential diagnosis in the assessment',
  },
  {
    label: 'Risk assessment',
    line: 'Document risk assessment and safety planning where relevant',
  },
  {
    label: 'Time-based billing',
    line: 'Document total visit time and time spent on counseling',
  },
  { label: 'Formal tone', line: 'Keep the tone formal and clinical' },
];

// Private Components

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    inline
    size="xs"
    weight="medium"
    className="tracking-wide text-text-tertiary uppercase"
  >
    {children}
  </Text>
);

/** Rendered inside ComboboxNewOption, where the typed value lives */
const UseTypedValueLabel: React.FC = () => {
  const { t } = useTranslation();
  const inputValue = useComboboxInputValue();
  return (
    <span className="flex items-center gap-2">
      <Icon name="Plus" size="sm" className="text-text-tertiary" />
      {t('outputTemplates.generateWithAi.useTypedValue', {
        value: inputValue.trim(),
      })}
    </span>
  );
};

/**
 * Filtered manually (shouldFilter would drop the free-text "Use …"
 * option, whose internal value never matches the search), same approach
 * as PatientSelector.
 */
const SuggestOptions: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
}> = ({ value, onChange, options }) => {
  const search = useComboboxInputValue().trim().toLowerCase();
  const filtered = options.filter((option) =>
    option.toLowerCase().includes(search),
  );
  const hasExactMatch = options.some(
    (option) => option.toLowerCase() === search,
  );

  return (
    <>
      {filtered.map((option) => (
        <ComboboxOption key={option} onSelect={() => onChange(option)}>
          <ComboboxSelectCheck selected={value === option} />
          {option}
        </ComboboxOption>
      ))}
      {!hasExactMatch && (
        <ComboboxNewOption onSelect={(typed) => onChange(typed.trim())}>
          <UseTypedValueLabel />
        </ComboboxNewOption>
      )}
    </>
  );
};

/**
 * Input-styled combobox: pick from the suggestions or type a custom
 * value and select the "Use …" option.
 */
const SuggestCombobox: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  disabled?: boolean;
}> = ({ value, onChange, options, placeholder, disabled }) => {
  const { t } = useTranslation();
  return (
    <Combobox disabled={disabled}>
      <ComboboxSelectButton className="w-full justify-between rounded-md border-border-primary px-3 py-2 text-sm">
        {value ? (
          <span className="truncate text-text-primary">{value}</span>
        ) : (
          <span className="text-text-tertiary">{placeholder}</span>
        )}
      </ComboboxSelectButton>
      <ComboboxDropdown
        searchPlaceholder={t('outputTemplates.generateWithAi.searchOrType')}
        shouldFilter={false}
      >
        <SuggestOptions value={value} onChange={onChange} options={options} />
      </ComboboxDropdown>
    </Combobox>
  );
};

/**
 * Dedicated "Generate with AI" page: the user describes their practice
 * (note format, specialty, visit type, length, free-form instructions
 * with one-click popular keywords) and we design a specialty-aware
 * template (Opus behind the scenes), landing them in the authoring
 * workspace with the generated sections pre-filled.
 */
export const GenerateWithAiPage: React.FC = () => {
  useCaptureEventOnMount('page_view_generate_template_with_ai');

  const { t } = useTranslation();
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const toast = useToast();
  const navigate = useNavigate();

  const [noteFormat, setNoteFormat] = useState<NoteFormat>('SOAP');
  const [specialty, setSpecialty] = useState('');
  const [visitType, setVisitType] = useState('');
  const [noteLength, setNoteLength] = useState<NoteLength>('balanced');
  const [instructions, setInstructions] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const noteFormats: {
    id: NoteFormat;
    expansion: string;
    description: string;
  }[] = [
    {
      id: 'SOAP',
      expansion: t('outputTemplates.generateWithAi.formatSoapExpansion'),
      description: t('outputTemplates.generateWithAi.formatSoapDescription'),
    },
    {
      id: 'BIRP',
      expansion: t('outputTemplates.generateWithAi.formatBirpExpansion'),
      description: t('outputTemplates.generateWithAi.formatBirpDescription'),
    },
    {
      id: 'DAP',
      expansion: t('outputTemplates.generateWithAi.formatDapExpansion'),
      description: t('outputTemplates.generateWithAi.formatDapDescription'),
    },
    {
      id: 'GIRP',
      expansion: t('outputTemplates.generateWithAi.formatGirpExpansion'),
      description: t('outputTemplates.generateWithAi.formatGirpDescription'),
    },
  ];

  const noteLengths: { id: NoteLength; label: string; description: string }[] =
    [
      {
        id: 'concise',
        label: t('outputTemplates.generateWithAi.lengthConciseLabel'),
        description: t(
          'outputTemplates.generateWithAi.lengthConciseDescription',
        ),
      },
      {
        id: 'balanced',
        label: t('outputTemplates.generateWithAi.lengthBalancedLabel'),
        description: t(
          'outputTemplates.generateWithAi.lengthBalancedDescription',
        ),
      },
      {
        id: 'detailed',
        label: t('outputTemplates.generateWithAi.lengthDetailedLabel'),
        description: t(
          'outputTemplates.generateWithAi.lengthDetailedDescription',
        ),
      },
    ];

  // Profile specialty pre-fills the field once loaded — but never
  // overrides something the user already picked
  const userId = useUserIdFromSession();
  const { specialty: userSpecialty } = useUserSpecialty(userId);
  useEffect(() => {
    if (userSpecialty) {
      setSpecialty((current) => current || userSpecialty);
    }
  }, [userSpecialty]);

  const canGenerate = specialty.trim().length > 0;

  const selectedLength = noteLengths.find((l) => l.id === noteLength)!;

  const instructionLines = instructions.split('\n');
  const toggleKeyword = (line: string) => {
    setInstructions((previous) => {
      const lines = previous.split('\n');
      if (lines.includes(line)) {
        return lines.filter((existing) => existing !== line).join('\n');
      }
      return previous.trim() ? `${previous.replace(/\n$/, '')}\n${line}` : line;
    });
  };

  const handleGenerate = async () => {
    if (!canGenerate || !tenantId || isGenerating) {
      return;
    }

    setError(null);
    setIsGenerating(true);

    try {
      const response = await apiClient.llm.generateTemplateBundleWithAi({
        params: { tenantId },
        body: {
          noteFormat,
          specialty: specialty.trim(),
          visitType: visitType.trim() || undefined,
          noteLength,
          instructions: instructions.trim() || undefined,
        },
      });

      if (response.status !== 201) {
        throw new Error(t('outputTemplates.generateWithAi.failedToGenerate'));
      }

      if (
        response.body.status === 'rejected' ||
        !response.body.outputTemplate
      ) {
        setIsGenerating(false);
        setError(
          response.body.reason ??
            t('outputTemplates.generateWithAi.unableToGenerate'),
        );
        return;
      }

      navigate('../new', {
        state: { generatedTemplate: response.body.outputTemplate },
      });
    } catch (generateError) {
      setIsGenerating(false);
      const message =
        generateError instanceof Error
          ? generateError.message
          : t('outputTemplates.common.somethingWentWrong');
      setError(message);
      toast.error(t('outputTemplates.common.generationFailed', { message }));
    }
  };

  return (
    <>
      <Header>
        <Link to=".." aria-label={t('outputTemplates.common.backToTemplates')}>
          <Button variant="ghost" size="sm" disabled={isGenerating}>
            <Icon name="ArrowLeft" size="md" />
          </Button>
        </Link>
        <div className="my-2 py-2">
          <Heading>{t('outputTemplates.generateWithAi.title')}</Heading>
        </div>
      </Header>
      <Content>
        {/* Match the patient pages' content inset */}
        <div className="py-3 md:p-8">
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-12">
            <Text color="tertiary">
              {t('outputTemplates.generateWithAi.intro')}
            </Text>

            <div className="flex flex-col gap-1.5">
              <FieldLabel>
                {t('outputTemplates.generateWithAi.noteFormatLabel')}
              </FieldLabel>
              <div className="grid grid-cols-2 gap-2">
                {noteFormats.map((format) => {
                  const isSelected = noteFormat === format.id;
                  return (
                    <button
                      key={format.id}
                      type="button"
                      disabled={isGenerating}
                      onClick={() => setNoteFormat(format.id)}
                      className={cn(
                        'flex flex-col gap-0.5 rounded-lg border p-3 text-left transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                        isSelected
                          ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary/30'
                          : 'border-border-primary hover:border-border-tertiary hover:bg-bg-secondary/60',
                      )}
                    >
                      <Text size="sm" weight="semibold">
                        {format.id}
                      </Text>
                      <Text size="xs" color="tertiary" className="block">
                        {format.expansion}
                      </Text>
                      <Text size="xs" color="secondary" className="block">
                        {format.description}
                      </Text>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel>
                {t('outputTemplates.generateWithAi.noteLengthLabel')}
              </FieldLabel>
              <div className="grid grid-cols-3 gap-2">
                {noteLengths.map((length) => (
                  <button
                    key={length.id}
                    type="button"
                    disabled={isGenerating}
                    onClick={() => setNoteLength(length.id)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                      noteLength === length.id
                        ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary/30 text-text-primary'
                        : 'border-border-primary text-text-secondary hover:border-border-tertiary hover:bg-bg-secondary/60',
                    )}
                  >
                    {length.label}
                  </button>
                ))}
              </div>
              <Text size="xs" color="tertiary">
                {selectedLength.description}
              </Text>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <FieldLabel>
                  {t('outputTemplates.generateWithAi.specialtyLabel')}
                </FieldLabel>
                <SuggestCombobox
                  value={specialty}
                  onChange={setSpecialty}
                  options={MEDICAL_SPECIALTIES}
                  placeholder={t(
                    'outputTemplates.generateWithAi.specialtyPlaceholder',
                  )}
                  disabled={isGenerating}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <FieldLabel>
                  {t('outputTemplates.generateWithAi.visitTypeLabel')}
                </FieldLabel>
                <SuggestCombobox
                  value={visitType}
                  onChange={setVisitType}
                  options={VISIT_TYPES}
                  placeholder={t(
                    'outputTemplates.generateWithAi.visitTypePlaceholder',
                  )}
                  disabled={isGenerating}
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <FieldLabel>
                {t('outputTemplates.generateWithAi.instructionsLabel')}
              </FieldLabel>
              <Textarea
                rows={8}
                value={instructions}
                disabled={isGenerating}
                onChange={(event) => setInstructions(event.target.value)}
                placeholder={t(
                  'outputTemplates.generateWithAi.instructionsPlaceholder',
                )}
                className="resize-none"
              />
              <div className="mt-1 flex flex-col gap-2">
                <Text size="xs" color="tertiary">
                  {t('outputTemplates.generateWithAi.popularKeywords')}
                </Text>
                <div className="flex flex-wrap gap-2">
                  {POPULAR_KEYWORDS.map((keyword) => {
                    const isActive = instructionLines.includes(keyword.line);
                    return (
                      <button
                        key={keyword.label}
                        type="button"
                        disabled={isGenerating}
                        onClick={() => toggleKeyword(keyword.line)}
                        className={cn(
                          'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                          isActive
                            ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary/30 text-text-primary'
                            : 'border-border-primary text-text-secondary hover:border-border-tertiary hover:bg-bg-secondary/60',
                        )}
                      >
                        {keyword.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-border-negative-subtle bg-bg-negative-subtle px-3 py-2">
                <Text size="xs" className="text-text-negative">
                  {error}
                </Text>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              {isGenerating && (
                <div className="flex items-center gap-2">
                  <LoadingIcon size="sm" />
                  <Text size="xs" color="secondary">
                    {t('outputTemplates.generateWithAi.designing')}
                  </Text>
                </div>
              )}
              <Button
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating}
              >
                {isGenerating
                  ? t('outputTemplates.common.generating')
                  : t('outputTemplates.common.generate')}
              </Button>
            </div>
          </div>
        </div>
      </Content>
    </>
  );
};
